import { supabase } from '../supabase';
import type { SnapCategory } from '@/types/database.types';

interface EnhancementRequest {
  question: string;
  questionDetails: string;
}

interface EnhancementResponse {
  output: {
    enhancedQuestion: string;
    enhancedQuestionDetails: string;
  };
}

export async function enhanceSnapText(question: string, description: string): Promise<EnhancementResponse['output']> {
  const request: EnhancementRequest = {
    question,
    questionDetails: description
  };

  try {
    const response = await fetch('https://progrezai.app.n8n.cloud/webhook/enhance-agent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.output;
  } catch (error) {
    console.error('Error enhancing text:', error);
    throw new Error('Failed to enhance text. Please try again later.');
  }
}

export async function getAiOpinion(
  snapId: string,
  feedbackModes: SnapCategory[]
): Promise<string> {
  const { data: snap } = await supabase
    .from('snaps')
    .select('*')
    .eq('id', snapId)
    .single();

  if (!snap) {
    throw new Error('SNAP not found');
  }

  const request = {
    question: snap.title,
    questionDetails: snap.description || '',
    categories: [snap.category],
    requestedFeedbackMode: feedbackModes
  };

  try {
    const response = await fetch('https://progrezai.app.n8n.cloud/webhook/mentor-agent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data || !data.output || typeof data.output !== 'string') {
      throw new Error('Invalid response format from AI service');
    }

    return data.output;
  } catch (error) {
    console.error('Error getting AI opinion:', error);
    throw new Error('Failed to get AI opinion. Please try again later.');
  }
}

interface GenerateOptionRequest {
  question: string;
  questionDetails: string;
  categories: SnapCategory[];
  requestedFeedbackMode: string;
}

interface GenerateOptionResponse {
  output: {
    newOption: string;
    newOptionDescription: string;
  };
}

export async function generateOptionWithAI(
  question: string,
  description: string,
  categories: SnapCategory[],
  requestedFeedbackMode: string
): Promise<GenerateOptionResponse['output']> {
  const request: GenerateOptionRequest = {
    question,
    questionDetails: description,
    categories,
    requestedFeedbackMode
  };

  try {
    const response = await fetch('https://progrezai.app.n8n.cloud/webhook/option-by-ai-agent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.output;
  } catch (error) {
    console.error('Error generating option:', error);
    throw new Error('Failed to generate option. Please try again later.');
  }
}